import { useScreen } from '@utils'
import { AnimatePresence, motion } from 'framer-motion'
import React from 'react'
import styled from 'styled-components'
import { AI_HELP_RESERVED_SPACE_CSS_VAR } from '@constants/layout'
import { Button } from '../Button'
import { FaArrowLeft } from 'react-icons/fa6'

type SideSheetPosition = 'left' | 'right'

interface Props {
  heading?: string
  isShown: boolean
  handleClose: () => void
  unsavedChanges?: boolean
  size?: 'sm' | 'md' | 'lg' | 'xl'
  position?: SideSheetPosition
  hideHeader?: boolean
  keepMounted?: boolean
  children: React.ReactNode
}

export const SideSheet: React.FC<Props> = ({
  children,
  isShown,
  heading,
  handleClose,
  size = 'md',
  position = 'right',
  hideHeader = false,
  keepMounted = false
}) => {
  const screen = useScreen()

  let width
  if (size === 'sm') width = screen.xs ? '100dvw' : screen.breakPoints.xs * 0.8
  if (size === 'md') width = screen.xs ? '100dvw' : screen.breakPoints.md * 0.8
  if (size === 'lg') width = screen.xs ? '100dvw' : screen.breakPoints.lg * 0.8
  if (size === 'xl') width = screen.xs ? '100dvw' : screen.breakPoints.xl * 0.8

  const shouldRenderSheet = isShown || keepMounted

  const sheet = shouldRenderSheet && (
    <StyledSideSheet
      as={motion.div}
      initial={false}
      animate={{
        x: isShown ? 0 : position === 'right' ? '100%' : '-100%',
        opacity: isShown ? 1 : 0
      }}
      transition={{ type: 'tween' }}
      style={{ width, pointerEvents: isShown ? 'auto' : 'none' }}
      aria-hidden={!isShown}
      $position={position}
    >
      <SheetContent>
        {!hideHeader && (
          <HeaderBar>
            <Button icon={<FaArrowLeft />} isSecondary={true} onClick={() => handleClose()} />
            <SheetHeading>{heading}</SheetHeading>
            <HeaderSpacer />
          </HeaderBar>
        )}

        <Body>{children}</Body>
      </SheetContent>
    </StyledSideSheet>
  )

  if (keepMounted) {
    return (
      <>
        <AnimatePresence>
          {isShown && <StyledBackDrop onClick={() => handleClose()} />}
        </AnimatePresence>
        {sheet}
      </>
    )
  }

  return (
    <AnimatePresence>
      {isShown && <StyledBackDrop onClick={() => handleClose()} />}
      {sheet}
    </AnimatePresence>
  )
}

const StyledBackDrop = styled(motion.div).attrs({
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 }
})`
  position: fixed;
  left: 0;
  right: var(${AI_HELP_RESERVED_SPACE_CSS_VAR}, 0px);
  top: 0;
  height: 100%;
  width: auto;
  background: ${({ theme }) => theme.colors.backdrop};
  z-index: 100;
`

const StyledSideSheet = styled.div.withConfig({
  shouldForwardProp: (prop: string) => prop !== '$position'
})<{ $position: SideSheetPosition }>`
  position: fixed;
  top: 0;
  right: ${({ $position }) =>
    $position === 'right' ? `var(${AI_HELP_RESERVED_SPACE_CSS_VAR}, 0px)` : 'auto'};
  left: ${({ $position }) => ($position === 'left' ? 0 : 'auto')};
  height: 100%;
  max-width: calc(100dvw - var(${AI_HELP_RESERVED_SPACE_CSS_VAR}, 0px));
  background: white;
  overflow-x: hidden;
  overflow-y: auto;
  z-index: 101;
`

const SheetContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
`

const HeaderBar = styled.div`
  display: flex;
  padding: 8px;
  background: #fff;
  border-bottom: 1px solid #e4e7eb;
`

const HeaderSpacer = styled.div`
  width: 48px;
`

const Body = styled.div`
  flex: 1;
  background: #fff;
  overflow-y: auto;
`

const SheetHeading = styled.h3`
  margin: 0;
  flex: 1;
  font-size: 20px;
  font-weight: 600;
  text-align: center;
  align-self: center;
  color: ${({ theme }) => theme.colors.neutralBlack};
`
